import { X, Crown, CreditCard, ArrowUpCircle, ExternalLink, Loader2 } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../lib/supabase';
import { useState } from 'react';

interface AccountModalProps {
  onClose: () => void;
  onUpgrade: () => void;
}

export function AccountModal({ onClose, onUpgrade }: AccountModalProps) {
  const { isPro, planName, subscription } = useSubscription();
  const [loadingPortal, setLoadingPortal] = useState(false);

  const handleManageBilling = async () => {
    const customerId = subscription?.stripe_customer_id;
    console.log('Stripe Billing Portal clicked. Customer ID from state:', customerId);
    
    setLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session');
      
      if (error) {
        console.error('Supabase function invocation error:', error);
        throw error;
      }

      if (data?.error) {
        console.error('API returned error:', data.error);
        alert(`Billing Portal Error: ${data.error}`);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No URL returned from portal session API');
      }
    } catch (err: any) {
      console.error('Portal error full details:', err);
      alert(err.message || 'Could not open billing portal. Please contact support.');
    } finally {
      setLoadingPortal(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#1a3e54]/60 backdrop-blur-md flex items-center justify-center p-4 z-[100] animate-fadeIn">
      <div className="bg-[#feeddd] rounded-[2.5rem] shadow-2xl max-w-md w-full p-8 relative animate-slideUp border border-[#1a3e54]/5">
        <button onClick={onClose} className="absolute top-6 right-6 text-[#1a3e54]/30 hover:text-[#1a3e54] transition-colors">
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 ${isPro ? 'bg-yellow-400' : 'bg-gray-200'}`}>
            <Crown className={`w-8 h-8 ${isPro ? 'text-white' : 'text-gray-400'}`} />
          </div>
          <h2 className="text-2xl font-playfair font-bold text-[#1a3e54]">Manage Account</h2>
          <p className="text-[#1a3e54]/60 text-sm mt-1">Manage your subscription and preferences</p>
        </div>

        <div className="space-y-4">
          {/* Plan Status */}
          <div className="bg-white rounded-2xl p-5 border border-[#1a3e54]/5 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-[#1a3e54]/30 mb-1">Current Plan</p>
              <p className="font-bold text-[#1a3e54] text-lg">{planName} Plan</p>
            </div>
            {isPro ? (
              <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold uppercase tracking-wider">
                Active
              </div>
            ) : (
              <button 
                onClick={() => {
                  onUpgrade();
                  onClose();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-[#9e142f] text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-[#801026] transition-all shadow-md"
              >
                <ArrowUpCircle className="w-4 h-4" />
                Upgrade
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <button 
              onClick={handleManageBilling}
              disabled={loadingPortal}
              className="w-full flex items-center justify-between p-5 bg-white rounded-2xl border border-[#1a3e54]/5 hover:bg-gray-50 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-[#1a3e54]">Billing & Invoices</p>
                  <p className="text-xs text-[#1a3e54]/40">Manage payment method or cancel</p>
                </div>
              </div>
              {loadingPortal ? (
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              ) : (
                <ExternalLink className="w-4 h-4 text-[#1a3e54]/20 group-hover:text-blue-600 transition-colors" />
              )}
            </button>

            <div className="p-5 text-center">
              <p className="text-[10px] font-bold text-[#1a3e54]/30 uppercase tracking-[0.2em]">
                Account ID: {subscription?.user_id?.slice(0, 8)}...
              </p>
            </div>
          </div>
        </div>

        <button 
          onClick={onClose}
          className="w-full mt-6 py-4 bg-[#1a3e54] text-[#feeddd] font-bold rounded-2xl hover:bg-[#2a5e74] transition-all"
        >
          Close
        </button>
      </div>
    </div>
  );
}